/**
 * Reusable wheel + button + slider zoom for any Recharts chart.
 *
 * Supports two modes:
 *   • "numeric"  — xDomain = [minVal, maxVal]. Use for charts whose XAxis is
 *                  `type="number"` (e.g. PayoffChart, GEXChart strike axis).
 *                  Wire `xAxisProps` into your <XAxis />.
 *   • "index"    — clips the data array to [startIdx, endIdx]. Use for
 *                  category / time-series axes (SymbolChart, KCESignalChart).
 *                  Wire `slicedData` as your chart's data prop.
 *
 * The hook also returns a `<Controls />` JSX-ready component (buttons +
 * 1×–50× slider + reset) and a `<ChartHost />` div that attaches the
 * non-passive wheel listener. Drop those in your chart's header / wrapper.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { ZoomIn, ZoomOut, RotateCcw } from "lucide-react";

const MIN_ZOOM = 1;
const MAX_ZOOM = 50;
const STEP_IN = 0.7;   // ×0.7 = ~43% zoom in per click
const STEP_OUT = 1.3;  // ×1.3 = ~30% zoom out per click

export function useChartZoom({
  data = [],
  xKey = "x",
  mode = "numeric",      // "numeric" | "index"
  anchorValue = null,    // optional default anchor (e.g. spot price)
  // Resets the zoom when this dep array's identity changes — pass the source
  // curve so a new build clears the user's zoom automatically.
  resetDeps = [],
} = {}) {
  // For numeric: xDomain is [min,max] in data units.
  // For index: xDomain is [startIdx, endIdx] integers into `data`.
  const [xDomain, setXDomain] = useState(null);
  const hoverRef = useRef(null);
  const wheelHostRef = useRef(null);

  const len = data?.length || 0;
  const fullMin = mode === "numeric" ? data?.[0]?.[xKey] : 0;
  const fullMax = mode === "numeric" ? data?.[len - 1]?.[xKey] : Math.max(0, len - 1);
  const fullSpan = (fullMax || 0) - (fullMin || 0);
  const activeMin = xDomain?.[0] ?? fullMin;
  const activeMax = xDomain?.[1] ?? fullMax;
  const activeSpan = (activeMax || 0) - (activeMin || 0);
  const zoomLevel = activeSpan > 0 ? fullSpan / activeSpan : 1;
  const isZoomed = xDomain != null;

  // Reset on curve change
  useEffect(() => {
    setXDomain(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, resetDeps);

  const applyZoom = (factor, anchor) => {
    if (!len || fullSpan <= 0) return;
    const a = anchor != null && anchor >= activeMin && anchor <= activeMax
      ? anchor
      : (activeMin + activeMax) / 2;
    let newLo = a - (a - activeMin) * factor;
    let newHi = a + (activeMax - a) * factor;
    if (newLo < fullMin) newLo = fullMin;
    if (newHi > fullMax) newHi = fullMax;
    if (mode === "index") {
      newLo = Math.max(0, Math.round(newLo));
      newHi = Math.min(len - 1, Math.round(newHi));
      if (newHi - newLo < 2) return; // need at least 3 points
    } else if (newHi - newLo < fullSpan * 0.02) {
      return;
    }
    if (newHi - newLo >= fullSpan * 0.998) {
      setXDomain(null);
    } else {
      setXDomain([newLo, newHi]);
    }
  };

  const setZoomLevel = (target) => {
    if (target <= 1.01 || !fullSpan) {
      setXDomain(null);
      return;
    }
    const newSpan = fullSpan / target;
    const a = hoverRef.current ?? anchorValue ?? (fullMin + fullMax) / 2;
    let lo = a - newSpan / 2;
    let hi = a + newSpan / 2;
    if (lo < fullMin) { hi += fullMin - lo; lo = fullMin; }
    if (hi > fullMax) { lo -= hi - fullMax; hi = fullMax; }
    if (mode === "index") {
      lo = Math.max(0, Math.round(lo));
      hi = Math.min(len - 1, Math.round(hi));
    }
    setXDomain([Math.max(fullMin, lo), Math.min(fullMax, hi)]);
  };

  // Attach non-passive wheel listener
  useEffect(() => {
    const host = wheelHostRef.current;
    if (!host) return undefined;
    const handler = (e) => {
      if (!len) return;
      if (Math.abs(e.deltaY) < Math.abs(e.deltaX)) return; // ignore horizontal trackpad
      e.preventDefault();
      const factor = e.deltaY < 0 ? STEP_IN : STEP_OUT;
      applyZoom(factor, hoverRef.current);
    };
    host.addEventListener("wheel", handler, { passive: false });
    return () => host.removeEventListener("wheel", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [len, activeMin, activeMax, mode]);

  // Slice helper for index mode
  const slicedData = useMemo(() => {
    if (mode !== "index" || !xDomain || !len) return data;
    const [s, e] = xDomain;
    return data.slice(Math.max(0, s), Math.min(len, e + 1));
  }, [data, xDomain, len, mode]);

  // For numeric mode, props to spread into <XAxis />
  const xAxisProps = mode === "numeric"
    ? { domain: xDomain || ["dataMin", "dataMax"], allowDataOverflow: true }
    : {};

  // For numeric mode, capture cursor's data x via Recharts mouse events
  const chartHandlers = mode === "numeric"
    ? {
        onMouseMove: (s) => {
          const p = s?.activePayload?.[0]?.payload?.[xKey];
          hoverRef.current = p ?? null;
        },
        onMouseLeave: () => { hoverRef.current = null; },
      }
    : {
        // For index mode, capture the active index
        onMouseMove: (s) => {
          hoverRef.current = s?.activeTooltipIndex ?? null;
        },
        onMouseLeave: () => { hoverRef.current = null; },
      };

  const reset = () => setXDomain(null);

  const Controls = ({ compact = false, anchor = null } = {}) => (
    <div
      className="flex items-center gap-1.5 flex-wrap"
      data-testid="chart-zoom-controls"
    >
      <button
        type="button"
        onClick={() => applyZoom(STEP_IN, hoverRef.current ?? anchor)}
        data-testid="chart-zoom-in"
        title="Zoom in (or scroll up on chart)"
        className="p-1 rounded border border-white/15 text-white/55 hover:text-white hover:border-white/35 transition"
      >
        <ZoomIn className="w-3 h-3" />
      </button>
      <button
        type="button"
        onClick={() => applyZoom(STEP_OUT, hoverRef.current ?? anchor)}
        data-testid="chart-zoom-out"
        title="Zoom out (or scroll down on chart)"
        className="p-1 rounded border border-white/15 text-white/55 hover:text-white hover:border-white/35 transition"
      >
        <ZoomOut className="w-3 h-3" />
      </button>
      {isZoomed && (
        <>
          <button
            type="button"
            onClick={reset}
            data-testid="chart-zoom-reset"
            title="Reset zoom"
            className="p-1 rounded border border-[#FFD60A]/40 text-[#FFD60A] hover:bg-[#FFD60A]/10 transition"
          >
            <RotateCcw className="w-3 h-3" />
          </button>
          <span
            className="text-[#FFD60A] num text-[10px]"
            data-testid="chart-zoom-level"
          >
            {zoomLevel.toFixed(1)}×
          </span>
        </>
      )}
      {!compact && len > 0 && (
        <div className="flex items-center gap-1 min-w-[110px] max-w-[200px] flex-1">
          <span className="text-white/30 text-[9px]">1×</span>
          <input
            type="range"
            min={MIN_ZOOM}
            max={MAX_ZOOM}
            step={0.5}
            value={Math.min(MAX_ZOOM, zoomLevel)}
            data-testid="chart-zoom-slider"
            onChange={(e) => setZoomLevel(parseFloat(e.target.value))}
            className="w-full accent-[#A98CFF] h-1"
          />
          <span className="text-white/30 text-[9px]">50×</span>
        </div>
      )}
    </div>
  );

  return {
    xDomain,
    setXDomain,
    zoomLevel,
    isZoomed,
    reset,
    applyZoom,
    setZoomLevel,
    hoverRef,
    wheelHostRef,
    slicedData,
    xAxisProps,
    chartHandlers,
    Controls,
  };
}
